import React, { createContext, useContext, useState, useCallback, useMemo } from "react";
import {
    DEMO_NET_WORTH,
    DEMO_NET_WORTH_HISTORY,
    DEMO_ACCOUNTS,
    DEMO_HOLDINGS,
    DEMO_EMERGENCY_FUND,
    DEMO_CONNECTION_HEALTH
} from "./demoData";

const DemoContext = createContext(null);

export function DemoProvider({ children }) {
    // Hidden accounts state
    const [hiddenAccountIds, setHiddenAccountIds] = useState(new Set());

    // Refreshing state for institutions - track per link
    const [refreshingLinkId, setRefreshingLinkId] = useState(null);
    const [linkRefreshTimes, setLinkRefreshTimes] = useState(() => {
        // Initialize with the times from demo data
        const times = {};
        DEMO_CONNECTION_HEALTH.links.forEach(link => {
            times[link.id] = new Date(link.lastSuccessfulSyncAt);
        });
        return times;
    });

    // Emergency fund state (allows demo interaction)
    const [emergencyFundData, setEmergencyFundData] = useState(DEMO_EMERGENCY_FUND);

    // Get visible accounts (excluding hidden)
    const visibleAccounts = useMemo(() =>
        DEMO_ACCOUNTS.filter(a => !hiddenAccountIds.has(a.accountId)),
        [hiddenAccountIds]
    );

    // Get visible holdings (only for visible accounts)
    const visibleHoldings = useMemo(() =>
        DEMO_HOLDINGS.filter(h => !hiddenAccountIds.has(h.accountId)),
        [hiddenAccountIds]
    );

    // Calculate net worth based on visible accounts
    const netWorthData = useMemo(() => {
        let cashBalance = 0;
        let investmentBalance = 0;
        let creditBalance = 0;
        let loanBalance = 0;

        visibleAccounts.forEach(account => {
            const balance = account.balances?.current || 0;
            const type = account.type?.toUpperCase();

            if (type === 'DEPOSITORY') {
                cashBalance += balance;
            } else if (type === 'INVESTMENT') {
                investmentBalance += balance;
            } else if (type === 'CREDIT') {
                creditBalance += balance;
            } else if (type === 'LOAN') {
                loanBalance += balance;
            }
        });

        const totalAssets = cashBalance + investmentBalance;
        const totalLiabilities = creditBalance + loanBalance;
        const netWorth = totalAssets - totalLiabilities;

        // Calculate change based on proportion of original
        const originalNetWorth = DEMO_NET_WORTH.netWorth;
        const proportion = netWorth / originalNetWorth;

        return {
            netWorth,
            totalAssets,
            totalLiabilities,
            cashBalance,
            investmentBalance,
            creditBalance,
            loanBalance,
            change: {
                amount: Math.round(DEMO_NET_WORTH.change.amount * proportion),
                percentage: DEMO_NET_WORTH.change.percentage,
                period: DEMO_NET_WORTH.change.period
            },
            calculatedAt: new Date().toISOString()
        };
    }, [visibleAccounts]);

    // Toggle account visibility
    const toggleAccountHidden = useCallback((accountId) => {
        setHiddenAccountIds(prev => {
            const next = new Set(prev);
            if (next.has(accountId)) {
                next.delete(accountId);
            } else {
                next.add(accountId);
            }
            return next;
        });
    }, []);

    // Refresh a connection (simulated) - updates only that link's time
    const refreshConnection = useCallback(async (linkId) => {
        setRefreshingLinkId(linkId);
        // Simulate API call delay
        await new Promise(resolve => setTimeout(resolve, 1500));
        setLinkRefreshTimes(prev => ({
            ...prev,
            [linkId]: new Date()
        }));
        setRefreshingLinkId(null);
    }, []);

    // Get refresh time for a specific link
    const getLinkRefreshTime = useCallback((linkId) => {
        return linkRefreshTimes[linkId] || new Date();
    }, [linkRefreshTimes]);

    // Update emergency fund based on visible cash
    const updateEmergencyFund = useCallback((newExpenses) => {
        const cashBalance = visibleAccounts
            .filter(a => a.type?.toUpperCase() === 'DEPOSITORY')
            .reduce((sum, a) => sum + (a.balances?.current || 0), 0);

        const monthsCovered = cashBalance / newExpenses;
        let status = "NOT_SET";
        if (monthsCovered >= 6) status = "HEALTHY";
        else if (monthsCovered >= 3) status = "BUILDING";
        else status = "LOW";

        setEmergencyFundData({
            ...emergencyFundData,
            cashBalance,
            monthlyExpenses: newExpenses,
            monthsCovered,
            status,
        });
    }, [emergencyFundData, visibleAccounts]);

    const value = {
        // Raw data
        allAccounts: DEMO_ACCOUNTS,
        allHoldings: DEMO_HOLDINGS,
        netWorthHistory: DEMO_NET_WORTH_HISTORY,
        connectionHealth: DEMO_CONNECTION_HEALTH,

        // Computed/filtered data
        visibleAccounts,
        visibleHoldings,
        hiddenAccountIds,
        netWorthData,

        // Emergency fund
        emergencyFundData,
        updateEmergencyFund,

        // Account visibility
        toggleAccountHidden,

        // Connection refresh
        refreshingLinkId,
        refreshConnection,
        getLinkRefreshTime,
    };

    return (
        <DemoContext.Provider value={value}>
            {children}
        </DemoContext.Provider>
    );
}

export function useDemo() {
    const context = useContext(DemoContext);
    if (!context) {
        throw new Error("useDemo must be used within a DemoProvider");
    }
    return context;
}
